import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { User, Calendar, CreditCard, ChevronRight } from "lucide-react";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-slate-200 text-slate-600",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [registrations, setRegistrations] = useState([]);

  useEffect(() => {
    api.get("/players/my").then(({ data }) => setPlayers(data.items || []));
    api.get("/registrations/my").then(({ data }) => setRegistrations(data.items || []));
  }, []);

  const handleRetryPayment = async (regId) => {
    try {
      const { data } = await api.post("/payments/checkout", {
        registration_id: regId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch {
      /* show in UI */
    }
  };

  return (
    <PublicLayout>
      <section className="bg-[#0A192F] text-white py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="label-overline text-white/60">Parent Dashboard</div>
          <h1 data-testid="dashboard-title" className="font-display text-4xl lg:text-5xl uppercase tracking-tighter mt-3">
            Welcome, {user?.name}
          </h1>
          <p className="text-sm text-white/70 mt-2">Manage your players, registrations, and CBR camp activity.</p>
        </div>
      </section>

      <section className="bg-[#F8F9FA] py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-3 gap-6">
          <div className="bg-white border border-slate-200 p-6 rounded-sm">
            <div className="flex items-center justify-between">
              <div className="label-overline">My Players</div>
              <User className="w-5 h-5 text-[#E63946]" />
            </div>
            <div className="font-data text-5xl mt-3">{players.length}</div>
          </div>
          <div className="bg-white border border-slate-200 p-6 rounded-sm">
            <div className="flex items-center justify-between">
              <div className="label-overline">Registrations</div>
              <Calendar className="w-5 h-5 text-[#E63946]" />
            </div>
            <div className="font-data text-5xl mt-3">{registrations.length}</div>
          </div>
          <div className="bg-white border border-slate-200 p-6 rounded-sm">
            <div className="flex items-center justify-between">
              <div className="label-overline">Confirmed</div>
              <CreditCard className="w-5 h-5 text-[#E63946]" />
            </div>
            <div className="font-data text-5xl mt-3">
              {registrations.filter((r) => r.status === "confirmed").length}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-display text-2xl uppercase tracking-tight">My Players</h2>
            <Link to="/camps" className="text-xs font-semibold uppercase tracking-wider text-[#0A192F] hover:text-[#E63946]">
              Browse Camps →
            </Link>
          </div>
          {players.length === 0 ? (
            <div className="border border-dashed border-slate-300 p-10 text-center">
              <p className="text-sm text-slate-600">
                You haven&apos;t added any players yet. Add one when you register for a camp.
              </p>
              <Link
                to="/camps"
                data-testid="dashboard-browse-camps-empty"
                className="inline-block mt-4 bg-[#0A192F] text-white px-6 py-3 text-xs font-semibold uppercase tracking-wider rounded-sm"
              >
                Browse Camps
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {players.map((p) => (
                <Link
                  key={p.id}
                  to={`/prospects/${p.slug}`}
                  data-testid={`dashboard-player-${p.id}`}
                  className="border border-slate-200 p-5 hover:border-[#0A192F] rounded-sm group"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="label-overline">{p.graduation_year || "—"} · {p.primary_position || "—"}</div>
                      <div className="font-display text-xl uppercase mt-1">{p.first_name} {p.last_name}</div>
                      <div className="text-xs text-slate-500 mt-1">{p.school || "—"} · {p.province || "—"}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-[#E63946]" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-[#F8F9FA] py-10 mt-6">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="font-display text-2xl uppercase tracking-tight mb-6">Registration History</h2>
          {registrations.length === 0 ? (
            <div className="bg-white border border-slate-200 p-10 text-center text-sm text-slate-500">
              No registrations yet.
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left p-4 label-overline">Camp</th>
                    <th className="text-left p-4 label-overline">Player</th>
                    <th className="text-left p-4 label-overline">Status</th>
                    <th className="text-left p-4 label-overline">Amount</th>
                    <th className="text-right p-4 label-overline">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {registrations.map((r) => (
                    <tr key={r.id} data-testid={`dashboard-reg-${r.id}`} className="border-b border-slate-100 last:border-0">
                      <td className="p-4">
                        <div className="font-semibold">{r.camp?.title || "—"}</div>
                        <div className="text-xs text-slate-500">
                          {r.camp ? new Date(r.camp.start_date).toLocaleDateString("en-CA", { dateStyle: "medium" }) : ""}
                        </div>
                      </td>
                      <td className="p-4">{r.player ? `${r.player.first_name} ${r.player.last_name}` : "—"}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-sm ${STATUS_STYLE[r.status] || "bg-slate-100"}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="p-4 font-data">${r.amount_due} {r.currency?.toUpperCase()}</td>
                      <td className="p-4 text-right">
                        {r.status === "pending" ? (
                          <button
                            onClick={() => handleRetryPayment(r.id)}
                            className="bg-[#E63946] text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm"
                          >
                            Pay Now
                          </button>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
